/**
 * Screen 5 — "Get the snippet onto your page": platform picker, the snippet
 * with the key interpolated, the site's CSP checked for the partner
 * (install/CspSection.tsx, with spec 013's live-mode sources only when the
 * published config asks for live conversation), and the email-to-developer
 * handoff.
 */
import { useState } from 'react';
import { useSession } from '../auth/session';
import { API_BASE, PORTAL_ORIGIN } from '../env';
import { CspSection } from '../install/CspSection';
import { PLATFORMS, developerEmail, platformBlocks } from '../install/snippets';
import { OnboardingPage } from '../setup/OnboardingPage';
import { SetupPathChooser } from '../agent/SetupPath';
import { useSites } from '../state/sites';
import { CodeBlock, Empty, Loading, Segmented } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { WidgetPageShare } from './WidgetPageShare';

export function InstallPanel() {
  const { api, org } = useSession();
  const { current } = useSites();
  const [platform, setPlatform] = useState('html');
  const [env, setEnv] = useState<'live' | 'test'>('live');
  const liveKey = current?.live.site_key ?? '';
  const origins = useQuery(() => api.listOrigins(liveKey), [liveKey], { enabled: Boolean(liveKey) });
  // Developers may read the PUBLISHED config (not drafts); that is what the page will run.
  const published = useQuery(() => api.getConfig(liveKey, 'published'), [liveKey], { enabled: Boolean(liveKey) });
  if (!current) return <Empty title="No site yet" />;
  const useTest = env === 'test' && Boolean(current.test);
  const key = useTest && current.test ? current.test.site_key : current.live.site_key;
  const modes = (published.data?.scope as { modes?: { live?: boolean; chat?: boolean } } | undefined)?.modes;
  const cardHeight = (published.data?.placement as { size?: { max_height_px?: number | null } } | undefined)?.size?.max_height_px ?? 640;
  const live = Boolean(modes?.live);
  const chat = Boolean(modes?.chat);
  const chosen = PLATFORMS.find((p) => p.id === platform) ?? PLATFORMS[0]!;
  const openOrigins = (origins.data?.items ?? []).filter((o) => !o.blocked_at).map((o) => o.origin);
  const mail = developerEmail({
    orgName: org?.display_name ?? 'our organisation',
    siteKey: current.live.site_key,
    origins: openOrigins,
    originPolicy: current.live.origin_policy ?? 'any',
    apiBase: API_BASE,
    live,
    chat,
    portalUrl: PORTAL_ORIGIN,
  });

  return (
    <div className="stack-lg">
      <section className="stack">
        <div className="row-between">
          <h2>1 · Your platform</h2>
          <Segmented
            label="Key"
            value={env}
            onChange={setEnv}
            options={[
              { value: 'live', label: 'Live key' },
              { value: 'test', label: 'Test key' },
            ]}
          />
        </div>
        <div className="chips" role="group" aria-label="Platform">
          {PLATFORMS.map((p) => (
            <button key={p.id} type="button" className="chip" aria-pressed={platform === p.id} onClick={() => setPlatform(p.id)}>
              {p.name}
            </button>
          ))}
        </div>
        {chosen.steps.length > 1 ? (
          <ol className="install-steps">
            {chosen.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        ) : (
          <p>{chosen.steps[0]}</p>
        )}
        {platformBlocks(chosen, key, { test: useTest }).map((b) => (
          <CodeBlock key={b.caption} caption={b.caption} code={b.code} />
        ))}
        <p className="small muted" data-testid="install-height">
          The widget is a fixed-height card: it takes <strong>{cardHeight} px</strong> of height where you paste it (never taller
          than a phone screen) and reserves that space as soon as the script loads, so your page doesn’t move. Leave that much room
          in the layout; you can change the height under Widget settings → Placement.
        </p>
      </section>
      <section className="stack-sm" aria-labelledby="install-csp">
        <h2 id="install-csp">2 · Content-Security-Policy</h2>
        <CspSection siteKey={liveKey} origins={openOrigins} live={live} chat={chat} configLoading={published.loading || origins.loading} />
      </section>
      <section className="panel panel-pad row-between">
        <div className="stack-sm" style={{ gap: 2 }}>
          <h3>Someone else installs it?</h3>
          <p className="small muted">Send them the snippet and the CSP in one email.</p>
        </div>
        <a className="btn" href={`mailto:?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`}>
          Email these instructions
        </a>
      </section>
      {/* No website to paste it on, or want a link and QR code too: the hosted widget page. */}
      <WidgetPageShare />
    </div>
  );
}

export function Install() {
  const { loading } = useSites();
  return (
    <OnboardingPage slug="install" lede="Paste two lines where the widget should appear. It fills the space you give it, with the card centred and up to 420 px wide (or the maximum you set under Placement).">
      {loading ? (
        <Loading />
      ) : (
        <SetupPathChooser
          manualDescription="Pick your platform and copy two lines. We check your site’s Content-Security-Policy for you."
          manual={<InstallPanel />}
        />
      )}
    </OnboardingPage>
  );
}
